import type { GameId } from './games.js';

/**
 * What the rules need to know about one version of a game: the publisher's facts, plus whether an
 * admin has blocked it. The catalog (`GameVersions` table) stores these; new versions arrive from
 * the publisher's list and never need adding by hand.
 */
export interface VersionFacts {
  /** The publisher's kind of version (Minecraft: release, snapshot, old_beta, old_alpha). */
  readonly type: string;
  /** When the publisher released it (ISO 8601); orders versions, as their names don't sort. */
  readonly releasedAt: string;
  /** Minecraft: the Java major version it needs (Mojang's `javaVersion.majorVersion`). */
  readonly javaMajor?: number;
  /** An admin took it off the list (a broken release); servers already on it keep running. */
  readonly block?: VersionBlock;
}

export interface VersionBlock {
  /** Why, for other admins (e.g. "breaks RCON"). */
  readonly reason: string;
  /** The admin's user ID. */
  readonly by: string;
  readonly at: string;
}

/**
 * One version in a game's catalog (`hearth-<env>-GameVersions`: `game` + `version`). The catalog
 * keeps the versions the rules support (blocked or not) and each newest version of any kind, with
 * the Java it needs; others aren't stored.
 */
export interface GameVersionRecord extends VersionFacts {
  readonly game: GameId;
  readonly version: string;
  /** When Hearth first saw it in the publisher's list. */
  readonly firstSeenAt: string;
}

/** When a game's catalog was last checked against the publisher's list. */
export interface CatalogCheck {
  readonly checkedAt: string;
  /** The list's `Last-Modified` then, sent back to learn whether it changed. */
  readonly lastModified?: string;
}

/**
 * Which versions of a game Hearth runs. Changed only by a PR, when what Hearth supports changes
 * (e.g. adapter work for older versions, or an image for a new Java version).
 */
export interface VersionRules {
  /** The oldest version Hearth runs; anything released before it is unsupported. */
  readonly floor: { readonly version: string; readonly releasedAt: string };
  /** The kinds of version offered. */
  readonly types: readonly string[];
  /** The container image for each Java major version, pinned (never `latest`). */
  readonly images: Readonly<Record<number, string>>;
}

/** The `itzg/minecraft-server` release the images are pinned to; move it on purpose, in a PR. */
const MINECRAFT_IMAGE = 'docker.io/itzg/minecraft-server:2026.10.0';

export const VERSION_RULES: Readonly<Record<GameId, VersionRules>> = {
  'minecraft-java': {
    // The adapter needs the modern server list ping (1.7+). 1.7 and 1.7.1 are snapshots in
    // Mojang's list; 1.7.2 is the first 1.7 release.
    floor: { version: '1.7.2', releasedAt: '2013-10-25T13:00:00+00:00' },
    // Snapshots aren't offered until rollback exists (see Deferred in CLAUDE.md).
    types: ['release'],
    // Every Java version a release from the floor on needs (8, 16, 17, 21, 25 as of 26.3).
    images: {
      8: `${MINECRAFT_IMAGE}-java8`,
      16: `${MINECRAFT_IMAGE}-java16`,
      17: `${MINECRAFT_IMAGE}-java17`,
      21: `${MINECRAFT_IMAGE}-java21`,
      25: `${MINECRAFT_IMAGE}-java25`,
    },
  },
};

/** Why a version isn't offered: outside the rules, blocked by an admin, or no image runs it yet. */
export type UnavailableReason = 'unsupported' | 'blocked' | 'needs-image';

export type VersionAvailability =
  | { readonly available: true; readonly image: string }
  | { readonly available: false; readonly reason: UnavailableReason };

/**
 * The image that runs a version, whatever its type, or undefined when Hearth has none for the
 * Java it needs. The refresh also asks this of snapshots: Mojang raises Java there first.
 */
export function versionImage(game: GameId, version: VersionFacts): string | undefined {
  return version.javaMajor === undefined ? undefined : VERSION_RULES[game].images[version.javaMajor];
}

/** Whether the rules allow a version (its kind, and not older than the floor), blocks and images aside. */
export function isSupported(game: GameId, version: Pick<VersionFacts, 'type' | 'releasedAt'>): boolean {
  const rules = VERSION_RULES[game];
  return rules.types.includes(version.type) && Date.parse(version.releasedAt) >= Date.parse(rules.floor.releasedAt);
}

/** Whether a version may be chosen for a new server or an upgrade, and the image that runs it. */
export function versionAvailability(game: GameId, version: VersionFacts): VersionAvailability {
  if (!isSupported(game, version)) return { available: false, reason: 'unsupported' };
  if (version.block) return { available: false, reason: 'blocked' };
  const image = versionImage(game, version);
  return image ? { available: true, image } : { available: false, reason: 'needs-image' };
}
